import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import React, { useState } from "react";
import { IoChevronDown } from "react-icons/io5";
import { useNavigate } from "react-router-dom"; // Import the useNavigate hook
import Loader from "@mui/material/CircularProgress"; // Material-UI CircularProgress

function Navbar() {
  const queryClient = useQueryClient();
  const API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate(); // Initialize useNavigate hook for programmatic navigation
  const [visibleDropdown, setVisibleDropdown] = useState(null); // State to manage visible dropdowns

  const [nestedDropdown, setNestedDropdown] = useState(null); // Manage nested dropdown visibility

  const {
    mutate: logout,
    isPending: isLoggingOut, // Is pending state for the mutation
  } = useMutation({
    mutationFn: async () => {
      try {
        const res = await fetch(`${API_URL}/auth/logout`, {
          method: "POST",
          credentials: "include",
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || "Something went wrong");
        }
      } catch (error) {
        throw new Error(error);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["authUser"] });
    },
    onError: () => {
      toast.error("Logout failed");
    },
  });

  // Fetching authentication user
  const { data: authUser, isLoading } = useQuery({ queryKey: ["authUser"] });

  // Delayed navigation handler
  const handleNavigation = (e, path) => {
    e.preventDefault(); // Prevent the default navigation

    if (isLoading) {
      return;
    }

    navigate(path); // Navigate only when authUser is loaded and isLoading is false
  };

  const toggleDropdown = (dropdownName) => {
    setVisibleDropdown((prev) => (prev === dropdownName ? null : dropdownName));
    setNestedDropdown(null); // Reset nested dropdown when parent dropdown changes
  };
  const toggleNestedDropdown = (dropdownName) => {
    setNestedDropdown((prev) => (prev === dropdownName ? null : dropdownName));
  };
  return (
    <nav className="hidden lg:flex w-full bg-gradient-to-r from-indigo-600 to-indigo-500 text-white">
      <div className="container mx-auto py-2 flex items-center justify-center">
        {/* Navbar Items */}
        <ul className="flex gap-4 font-normal items-center relative">
          <li className="cursor-pointer">
            <a href="/" onClick={(e) => handleNavigation(e, "/")}>Home</a>
          </li>

          {/* About Us with Dropdown */}
          <li className="relative group cursor-pointer">
            <span className="flex items-center gap-1">About Us <IoChevronDown /></span>
            {/* Dropdown Content */}
            <ul className="absolute hidden group-hover:block bg-white text-gray-800 shadow-md rounded w-48 z-50">
              <li className="hover:bg-gray-200 px-4 py-2 cursor-pointer">
                <a href="/about/immunocon-2025" onClick={(e) => handleNavigation(e, "/about/immunocon-2025")}>About the 52nd Immunocon</a>
              </li>
              <li className="hover:bg-gray-200 px-4 py-2 cursor-pointer">
                <a href="/about/iis" onClick={(e) => handleNavigation(e, "/about/iis")}>About IIS</a>
              </li>
              <li className="hover:bg-gray-200 px-4 py-2 cursor-pointer">
                <a href="/about/mnnit" onClick={(e) => handleNavigation(e, "/about/mnnit")}>About MNNIT</a>
              </li>
              <li className="hover:bg-gray-200 px-4 py-2 cursor-pointer">
                <a href="/about/prayagraj" onClick={(e) => handleNavigation(e, "/about/prayagraj")}>About Prayagraj</a>
              </li>
              <li className="hover:bg-gray-200 px-4 py-2 cursor-pointer">
                <a href="/about/organizing-department" onClick={(e) => handleNavigation(e, "/about/organizing-department")}>Organizing department</a>
              </li>
              <li className="hover:bg-gray-200 px-4 py-2 cursor-pointer">
                <a href="/about/past-conferences" onClick={(e) => handleNavigation(e, "/about/past-conferences")}>Past Immunocon conferences</a>
              </li>
            </ul>
          </li>

          <li className="relative group cursor-pointer">
            <span className="flex items-center gap-1">Committee <IoChevronDown /></span>
            {/* Dropdown Content */}
            <ul className="absolute hidden group-hover:block bg-white text-gray-800 shadow-md rounded w-48 z-50">
              <li className="hover:bg-gray-200 px-4 py-2 cursor-pointer">
                <a href="/committee/national-advisory" onClick={(e) => handleNavigation(e, "/committee/national-advisory")}>National Advisory Committee</a>
              </li>
              <li className="hover:bg-gray-200 px-4 py-2 cursor-pointer">
                <a href="/committee/scientific-program-advisory" onClick={(e) => handleNavigation(e, "/committee/scientific-program-advisory")}>Scientific Program Advisory Committee</a>
              </li>
              <li className="hover:bg-gray-200 px-4 py-2 cursor-pointer">
                <a href="/committee/organizing" onClick={(e) => handleNavigation(e, "/committee/organizing")}>Organizing Committee</a>
              </li>
              <li className="hover:bg-gray-200 px-4 py-2 cursor-pointer">
                <a href="/local-advisory" onClick={(e) => handleNavigation(e, "/local-advisory")}>Local advisory committee</a>
              </li>
            </ul>
          </li>
          <li className="cursor-pointer">
            <a href="/conference-registration/abstract" onClick={(e) => handleNavigation(e, "/conference-registration/abstract")}>Abstract Submission</a>
          </li>
          <li className="relative group cursor-pointer">
            <a href="/register" onClick={(e) => handleNavigation(e, "/register")}>Registration</a>
          </li>
{/* Awards Dropdown */}
<li className="relative cursor-pointer">
            <span
              className="flex items-center gap-1"
              onClick={() => toggleDropdown("awards")}
            >
              Awards <IoChevronDown />
            </span>
            {visibleDropdown === "awards" && (
              <ul className="absolute bg-white text-gray-800 shadow-md rounded w-60 z-50">
                {/* IIS Awards Submenu */}
                <li className="relative cursor-pointer">
                  <span
                    className="flex items-center px-4 py-2 hover:bg-gray-200"
                    onClick={() => toggleNestedDropdown("iis-awards")}
                  >
                    IIS Awards <IoChevronDown />
                  </span>
                  {nestedDropdown === "iis-awards" && (
                    <ul className="absolute left-full top-0 bg-white text-gray-800 shadow-md rounded w-64 z-50">
                      <li className="hover:bg-gray-200 px-4 py-2 cursor-pointer">
                        <a href="/senior-scientist" onClick={(e) => handleNavigation(e, "/senior-scientist")}>
                          IIS Senior Scientist Award
                        </a>
                      </li>
                      <li className="hover:bg-gray-200 px-4 py-2 cursor-pointer">
                        <a href="/mid-career" onClick={(e) => handleNavigation(e, "/mid-career")}>
                          IIS Mid-Career Scientist Award
                        </a>
                      </li>
                      <li className="hover:bg-gray-200 px-4 py-2 cursor-pointer">
                        <a href="/gp-talwar" onClick={(e) => handleNavigation(e, "/gp-talwar")}>
                        G.P. Talwar Young Scientist Awards
                        </a>
                      </li>
                      <li className="hover:bg-gray-200 px-4 py-2 cursor-pointer">
                        <a href="/student-travel" onClick={(e) => handleNavigation(e, "/student-travel")}>
                        Student Travel Bursaries
                        </a>
                      </li>
                      <li className="hover:bg-gray-200 px-4 py-2 cursor-pointer">
                        <a href="/translational" onClick={(e) => handleNavigation(e, "/translational")}>
                         Translational Immunol & Academia-Industry Award

                        </a>
                      </li>
                      {/* Add more nested items here */}
                    </ul>
                  )}
                </li>

                {/* Immunocon-2025 Awards Submenu */}
                <li className="relative cursor-pointer">
                  <span
                    className="flex items-center px-4 py-2 hover:bg-gray-200"
                    onClick={() => toggleNestedDropdown("immunocon-awards")}
                  >
                    Immunocon-2025 Awards <IoChevronDown />
                  </span>
                  {nestedDropdown === "immunocon-awards" && (
                    <ul className="absolute left-full top-0 bg-white text-gray-800 shadow-md rounded w-64 z-50">
                      <li className="hover:bg-gray-200 px-4 py-2 cursor-pointer">
                        <a href="/travel-awards" onClick={(e) => handleNavigation(e, "/travel-awards")}>
                          Immunocon-2025 Travel Awards
                        </a>
                      </li>
                      <li className="hover:bg-gray-200 px-4 py-2 cursor-pointer">
                        <a href="/poster-presentation" onClick={(e) => handleNavigation(e, "/poster-presentation")}>
                          Poster Presentation Awards
                        </a>
                      </li>
                      {/* Add more nested items here */}
                    </ul>
                  )}
                </li>
              </ul>
            )}
          </li>
          <li className="cursor-pointer">
          <a href="/accommodation" onClick={(e) => handleNavigation(e, "/accommodation")}>Accommodation</a>
          </li>
          <li className="cursor-pointer">
            <a href="/program-schedule" onClick={(e) => handleNavigation(e, "/program-schedule")}>Program Schedule</a>
          </li>
          <li className="cursor-pointer">
          <a href="/sponsor" onClick={(e) => handleNavigation(e, "/sponsor")}>Sponsors</a>
          </li>

          {/* Conditional Log In/Out */}
          {authUser ? (
            <button
              className="cursor-pointer w-20 bg-red-600 px-2 py-1 rounded flex items-center justify-center"
              onClick={(e) => {
                e.preventDefault();
                logout();
              }}
            >
              {isLoggingOut ? (
                <Loader sx={() => ({ color: "#ffffff" })} size={24} className="text-white" />
              ) : (
              "Log out"
              )}
            </button>
          ) : (
            <li className="cursor-pointer bg-blue-600 px-2 py-1 rounded">
              <a href="/login" onClick={(e) => handleNavigation(e, "/login")}>Log in</a>
            </li>
          )}
        </ul>
      </div>
    </nav>
  );
}

export default Navbar;
